import React, { useRef, useEffect } from 'react';
import { Bold, Italic, Underline, List, ListOrdered } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  required?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  value, 
  onChange, 
  placeholder = "Metin giriniz...",
  minHeight = "100px",
  required = false
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const isUserTyping = useRef(false);

  // İlk yükleme ve değer değişikliklerinde içeriği güncelle
  useEffect(() => {
    if (editorRef.current && !isUserTyping.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
  }, [value]);

  // Her tuş vuruşunda ve input değişikliğinde direction'ı zorla
  const handleInput = () => {
    if (editorRef.current) {
      isUserTyping.current = true;
      
      // Direction'ı zorla
      editorRef.current.style.direction = 'ltr';
      editorRef.current.style.textAlign = 'left';
      editorRef.current.setAttribute('dir', 'ltr');
      
      // İçeriği parent'a gönder
      const html = editorRef.current.innerHTML;
      onChange(html);
      
      setTimeout(() => {
        isUserTyping.current = false;
      }, 100);
    }
  };

  // Toolbar komutları
  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  // Focus olduğunda direction'ı zorla
  const handleFocus = () => {
    if (editorRef.current) {
      editorRef.current.style.direction = 'ltr';
      editorRef.current.style.textAlign = 'left';
      editorRef.current.setAttribute('dir', 'ltr');
    }
  };

  // Paste olayında direction'ı zorla
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
    handleInput();
  };

  // Keyboard olaylarında direction'ı zorla
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (editorRef.current) {
      // Her tuşta direction'ı zorla
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.style.direction = 'ltr';
          editorRef.current.style.textAlign = 'left';
        }
      }, 0);
    }
  };

  return (
    <div className="border border-input rounded-md overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b bg-muted/20 flex-wrap">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-1.5 hover:bg-background rounded transition-colors"
          title="Kalın (Ctrl+B)"
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-1.5 hover:bg-background rounded transition-colors"
          title="İtalik (Ctrl+I)"
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-1.5 hover:bg-background rounded transition-colors"
          title="Altı Çizili (Ctrl+U)"
        >
          <Underline className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-border mx-1"></div>
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="p-1.5 hover:bg-background rounded transition-colors"
          title="Madde İşaretli Liste"
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="p-1.5 hover:bg-background rounded transition-colors"
          title="Numaralı Liste"
        >
          <ListOrdered className="w-4 h-4" />
        </button>
      </div>

      {/* Editable Area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        dir="ltr"
        onInput={handleInput}
        onFocus={handleFocus}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        className="p-3 focus:outline-none text-sm rich-text-content"
        style={{ 
          minHeight,
          direction: 'ltr',
          textAlign: 'left',
          unicodeBidi: 'plaintext',
          wordBreak: 'break-word'
        }}
      />
      
      {/* Hidden input for form validation */}
      {required && (
        <input
          type="text"
          value={value}
          onChange={() => {}}
          required
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
};

export default RichTextEditor;


